import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import { Plus, Search, Edit2, Trash2, Users, School, BookOpen, User, Bookmark, Eye, ArrowLeft } from 'lucide-react';
import api from '../components/api';
import Modal from '../components/Modal';
import FormInput from '../components/FormInput';
import ConfirmationModal from '../components/ConfirmationModal';
import { getDeleteConfirmation } from '../utils/confirmationUtils';
import { useNavigate } from 'react-router-dom';

const ClassePage = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClasse, setSelectedClasse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    niveau: '',
    filiere: '',
    libelle: '',
    annee: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0
  });
  const [serverPagination, setServerPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1
  });

  // État pour gérer la modal de confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, [serverPagination.page]);

  useEffect(() => {
    // Générer automatiquement le libellé à partir du niveau et de la filière
    if (formData.niveau && formData.filiere) {
      const niveau = niveaux.find(n => n.id.toString() === formData.niveau.toString());
      const filiere = filieres.find(f => f.id.toString() === formData.filiere.toString());
      
      if (niveau && filiere) {
        setFormData(prev => ({
          ...prev,
          libelle: `${niveau.libelle} ${filiere.libelle}`
        }));
      }
    }
  }, [formData.niveau, formData.filiere, niveaux, filieres]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Utiliser la pagination côté serveur
      const [
        classesResponse, 
        niveauxResponse, 
        filieresResponse, 
        anneesResponse
      ] = await Promise.all([
        api.get(`/classe/classe-list-create/?page=${serverPagination.page}`),
        api.get('/niveau/liste-niveaux/'),
        api.get('/filiere/liste-filieres-all/'),
        api.get('/etablissement/annees-academiques/')
      ]);

      console.log('Niveaux chargés:', niveauxResponse.data);
      console.log('Filières chargées:', filieresResponse.data);
      console.log('Années académiques chargées:', anneesResponse.data);
      console.log('Classes chargées:', classesResponse.data);
      
      // Si réponse paginée du backend avec count, next, previous, results
      if (classesResponse.data.results) {
        setClasses(classesResponse.data.results);
        setServerPagination({
          count: classesResponse.data.count,
          next: classesResponse.data.next,
          previous: classesResponse.data.previous,
          page: serverPagination.page
        });
      } else {
        // Fallback si le backend ne supporte pas encore la pagination
        setClasses(classesResponse.data);
      }
      
      setNiveaux(niveauxResponse.data);
      setFilieres(filieresResponse.data);
      setAnnees(anneesResponse.data);
      
      // Mise à jour de la pagination côté client (si nécessaire)
      setPagination(prev => ({
        ...prev,
        totalItems: classesResponse.data.count || classesResponse.data.length,
        totalPages: Math.ceil((classesResponse.data.count || classesResponse.data.length) / prev.itemsPerPage)
      }));
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Convertir en nombre si c'est un champ numérique (niveau, filiere, annee)
    const numericFields = ['niveau', 'filiere', 'annee'];
    
    let newValue = value;
    if (numericFields.includes(name) && value !== '') {
      newValue = Number(value);
      console.log(`Conversion automatique de ${name}: ${value} (${typeof value}) -> ${newValue} (${typeof newValue})`);
    }
    
    setFormData({ ...formData, [name]: newValue });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined
      });
    }
  };

  const handleOpenModal = (classe = null) => {
    if (classe) {
      // Convertir explicitement les IDs en nombres
      setFormData({
        niveau: classe.niveau?.id ? Number(classe.niveau.id) : '',
        filiere: classe.filiere?.id ? Number(classe.filiere.id) : '',
        libelle: classe.libelle,
        annee: classe.annee?.id ? Number(classe.annee.id) : ''
      });
      setSelectedClasse(classe);
    } else {
      // Pour une nouvelle classe, récupérer l'année active par défaut
      const anneeActive = annees.find(a => a.status === true);
      setFormData({
        niveau: '',
        filiere: '',
        libelle: '',
        annee: anneeActive?.id ? Number(anneeActive.id) : ''
      });
      setSelectedClasse(null);
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.niveau) {
      errors.niveau = 'Le niveau est requis';
    }
    
    if (!formData.filiere) {
      errors.filiere = 'La filière est requise';
    }
    
    if (!formData.libelle.trim()) {
      errors.libelle = 'Le libellé de la classe est requis';
    }
    
    if (!formData.annee) {
      errors.annee = 'L\'année académique est requise';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || isSubmitting) return;
    
    // Éviter les soumissions multiples
    setIsSubmitting(true);
    
    try {
      // Convertir explicitement les valeurs en entiers
      const niveau = parseInt(formData.niveau, 10);
      const filiere = parseInt(formData.filiere, 10);
      const annee = parseInt(formData.annee, 10);
      
      console.log('Valeurs converties :', { niveau, filiere, annee });
      console.log('Types des valeurs:', {
        niveau: typeof niveau,
        filiere: typeof filiere,
        annee: typeof annee
      });
      
      // Vérifier que les valeurs numériques sont correctes
      if (isNaN(niveau) || niveau <= 0) {
        setFormErrors(prev => ({ ...prev, niveau: 'Niveau invalide' }));
        setIsSubmitting(false);
        return;
      }
      
      if (isNaN(filiere) || filiere <= 0) {
        setFormErrors(prev => ({ ...prev, filiere: 'Filière invalide' }));
        setIsSubmitting(false);
        return;
      }
      
      if (isNaN(annee) || annee <= 0) {
        setFormErrors(prev => ({ ...prev, annee: 'Année invalide' }));
        setIsSubmitting(false);
        return;
      }
      
      // Utiliser FormData pour l'envoi au backend
      const formDataToSend = new FormData();
      formDataToSend.append('libelle', formData.libelle.trim());
      formDataToSend.append('niveau', niveau);
      formDataToSend.append('filiere', filiere);
      formDataToSend.append('annee', annee);
      
      // Préparer également un objet JSON standard comme alternative
      const jsonData = {
        libelle: formData.libelle.trim(),
        niveau,
        filiere,
        annee
      };
      
      console.log('FormData à envoyer:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}: ${value} (${typeof value})`);
      }
      console.log('JSON à envoyer:', jsonData);
      
      let response;
      
      if (selectedClasse) {
        // Pour la mise à jour, utiliser JSON standard
        response = await api.put(`/classe/details-classe/${selectedClasse.id}/`, jsonData);
        toast.success('Classe modifiée avec succès');
      } else {
        // Pour la création, essayer FormData
        try {
          response = await api.post('/classe/classe-list-create/', formDataToSend, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (formDataError) {
          console.error('Erreur avec FormData, essai avec JSON:', formDataError);
          // Si FormData échoue, essayer avec JSON
          response = await api.post('/classe/classe-list-create/', jsonData);
        }
        toast.success('Classe créée avec succès');
      }
      
      console.log('Réponse du serveur:', response.data);
      
      setIsModalOpen(false);
      fetchInitialData();
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      console.error('Message d\'erreur:', error.message);
      console.error('Réponse du serveur:', error.response?.data);
      console.error('Status de l\'erreur:', error.response?.status);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        console.error('Détails de l\'erreur:', errorData);
        
        if (typeof errorData === 'object') {
          // Transform API validation errors to match our form
          const apiErrors = {};
          Object.entries(errorData).forEach(([field, message]) => {
            apiErrors[field] = Array.isArray(message) ? message[0] : message;
          });
          setFormErrors(apiErrors);
          
          // Message d'erreur plus descriptif
          if (errorData.niveau || errorData.filiere || errorData.annee) {
            toast.error('Les champs Niveau, Filière ou Année académique sont invalides ou manquants');
          } else {
            toast.error('Erreur de validation. Veuillez vérifier les champs du formulaire.');
          }
        } else {
          toast.error('Erreur lors de la sauvegarde de la classe');
        }
      } else {
        toast.error('Erreur lors de la sauvegarde de la classe');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteModal = (classe) => {
    setClassToDelete(classe);
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!classToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/classe/details-classe/${classToDelete.id}/`);
      toast.success('Classe supprimée avec succès');
      fetchInitialData();
      setIsConfirmModalOpen(false);
      setClassToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression de la classe');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Fonction pour gérer le changement de page
  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  // Fonction pour obtenir les éléments de la page courante
  const getCurrentPageItems = () => {
    const { currentPage, itemsPerPage } = pagination;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredClasses.slice(startIndex, endIndex);
  };

  const filteredClasses = classes.filter(classe =>
    classe.libelle.toLowerCase().includes(searchTerm.toLowerCase()) ||
    classe.niveau?.libelle.toLowerCase().includes(searchTerm.toLowerCase()) ||
    classe.filiere?.libelle.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination mise à jour lorsque les filtres changent
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalItems: filteredClasses.length,
      totalPages: Math.ceil(filteredClasses.length / prev.itemsPerPage)
    }));
  }, [filteredClasses.length, pagination.itemsPerPage]);

  const renderModalFooter = () => (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={() => setIsModalOpen(false)}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
        disabled={isSubmitting}
      >
        Annuler
      </button>
      <button
        type="submit"
        onClick={handleSubmit}
        className="px-4 py-2 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Traitement...' : (selectedClasse ? 'Modifier' : 'Créer')}
      </button>
    </div>
  );

  // Vérifier s'il y a des données chargées pour le formulaire
  const isDataLoaded = niveaux.length > 0 && filieres.length > 0 && annees.length > 0;

  // Debug du formulaire
  console.log('État actuel du formulaire:', formData);
  
  // Fonction pour changer de page (pagination serveur)
  const handleServerPageChange = (page) => {
    setServerPagination(prev => ({
      ...prev,
      page: page
    }));
  };

  // Utilisation de la fonction utilitaire pour obtenir la configuration de la confirmation
  const confirmConfig = classToDelete 
    ? getDeleteConfirmation(classToDelete, 'la classe', 'libelle') 
    : {};

  // Fonction pour naviguer vers la page de détails de la classe
  const handleViewClassDetails = (classeId) => {
    navigate(`/detail-classe/${classeId}`);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <School className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion des Classes</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les classes et leurs compositions
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleOpenModal()}
                  className="mt-4 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-[#202654] bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Nouvelle Classe
                </button>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                      <School className="h-6 w-6" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Classes</p>
                      <p className="text-2xl font-semibold text-gray-900">{classes.length}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-100 text-green-600">
                      <Users className="h-6 w-6" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Étudiants</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {classes.reduce((sum, classe) => sum + (classe.get_effectif || 0), 0)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                      <BookOpen className="h-6 w-6" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Moyenne par Classe</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {classes.length
                          ? Math.round(classes.reduce((sum, classe) => sum + (classe.get_effectif || 0), 0) / classes.length)
                          : 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="Rechercher une classe..."
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-[#202654] focus:border-[#202654]"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="flex justify-center items-center p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Classe
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Niveau
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Filière
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Effectif
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Année
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredClasses.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-6 py-12">
                              <div className="flex flex-col items-center justify-center text-center">
                                <School className="h-12 w-12 text-gray-400 mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-1">Aucune classe trouvée</h3>
                                <p className="text-sm text-gray-500 max-w-md">
                                  {searchTerm ? 'Essayez avec un autre terme de recherche' : 'Commencez par créer votre première classe'}
                                </p>
                                {!searchTerm && (
                                  <button
                                    onClick={() => handleOpenModal()}
                                    className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90"
                                  >
                                    <Plus className="h-5 w-5 mr-2" />
                                    Créer une classe
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ) : (
                          getCurrentPageItems().map((classe) => (
                            <tr key={classe.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="h-9 w-9 rounded-full bg-[#202654]/10 flex items-center justify-center mr-3">
                                    <School className="h-5 w-5 text-[#202654]" />
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {classe.libelle}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {classe.niveau ? (
                                  <div className="flex items-center">
                                    <Bookmark className="h-5 w-5 text-blue-500 mr-2" />
                                    <span className="text-sm text-gray-900">{classe.niveau.libelle}</span>
                                  </div>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Non défini
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {classe.filiere ? (
                                  <div className="flex items-center">
                                    <BookOpen className="h-5 w-5 text-purple-500 mr-2" />
                                    <span className="text-sm text-gray-900">{classe.filiere.libelle}</span>
                                  </div>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Non défini
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <User className="h-5 w-5 text-green-500 mr-2" />
                                  <span className="text-sm text-gray-900">
                                    {classe.get_effectif || 0} étudiants
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {classe.annee ? (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    classe.annee.status 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {classe.annee.libelle} {classe.annee.status ? "(Active)" : ""}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Non défini
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleViewClassDetails(classe.id)}
                                  className="text-blue-600 hover:text-blue-900 mr-3 p-1 rounded-full hover:bg-blue-50 transition-colors"
                                  title="Voir les détails"
                                >
                                  <Eye className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleOpenModal(classe)}
                                  className="text-indigo-600 hover:text-indigo-900 mr-3 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                                  title="Modifier"
                                >
                                  <Edit2 className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleOpenDeleteModal(classe)}
                                  className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                                  title="Supprimer"
                                >
                                  <Trash2 className="h-5 w-5" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Pagination serveur */}
                {filteredClasses.length > 0 && (
                  <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handleServerPageChange(Math.max(1, serverPagination.page - 1))}
                        disabled={!serverPagination.previous}
                        className={`
                          relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md 
                          ${!serverPagination.previous 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'}
                        `}
                      >
                        Précédent
                      </button>
                      <button
                        onClick={() => handleServerPageChange(serverPagination.page + 1)}
                        disabled={!serverPagination.next}
                        className={`
                          ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md 
                          ${!serverPagination.next 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'}
                        `}
                      >
                        Suivant
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Affichage des résultats - Total: <span className="font-medium">{serverPagination.count}</span> classes
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => handleServerPageChange(serverPagination.page - 1)}
                            disabled={!serverPagination.previous}
                            className={`
                              relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium
                              ${!serverPagination.previous 
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-500 hover:bg-gray-50'}
                            `}
                          >
                            <span className="sr-only">Précédent</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                          
                          <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            Page {serverPagination.page}
                          </span>
                          
                          <button
                            onClick={() => handleServerPageChange(serverPagination.page + 1)}
                            disabled={!serverPagination.next}
                            className={`
                              relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium
                              ${!serverPagination.next 
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-500 hover:bg-gray-50'}
                            `}
                          >
                            <span className="sr-only">Suivant</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedClasse ? 'Modifier la classe' : 'Nouvelle classe'}
        footer={renderModalFooter()}
      >
        {!isDataLoaded ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Niveau"
                type="select"
                name="niveau"
                value={formData.niveau}
                onChange={handleInputChange}
                options={[
                  { value: '', label: 'Sélectionner un niveau' },
                  ...niveaux.map(niveau => ({ 
                    value: Number(niveau.id),
                    label: niveau.libelle 
                  }))
                ]}
                required
                error={formErrors.niveau}
                icon={Bookmark}
              />
              
              <FormInput
                label="Filière"
                type="select"
                name="filiere"
                value={formData.filiere}
                onChange={handleInputChange}
                options={[
                  { value: '', label: 'Sélectionner une filière' },
                  ...filieres.map(filiere => ({ 
                    value: Number(filiere.id),
                    label: filiere.libelle 
                  }))
                ]}
                required
                error={formErrors.filiere}
                icon={BookOpen}
              />
            </div>
            
            <FormInput
              label="Libellé de la classe"
              type="text"
              name="libelle"
              value={formData.libelle}
              onChange={handleInputChange}
              placeholder="Libellé généré automatiquement, mais modifiable"
              required
              error={formErrors.libelle}
              icon={School}
              helpText="Le libellé est généré automatiquement à partir du niveau et de la filière, mais vous pouvez le personnaliser."
            />
            
            <FormInput
              label="Année académique"
              type="select"
              name="annee"
              value={formData.annee}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Sélectionner une année' },
                ...annees.map(annee => ({ 
                  value: Number(annee.id),
                  label: `${annee.libelle} ${annee.status ? "(Active)" : ""}` 
                }))
              ]}
              required
              error={formErrors.annee}
            />

            {/* Ajouter un champ caché pour déboguer si nécessaire */}
            {Object.keys(formErrors).length > 0 && (
              <div className="bg-red-50 p-4 rounded-md">
                <h3 className="text-sm font-medium text-red-800">Erreurs de validation</h3>
                <ul className="mt-2 text-xs text-red-700">
                  {Object.entries(formErrors).map(([field, error]) => (
                    error && <li key={field}>{field}: {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </form>
        )}
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer la classe"}
        message={confirmConfig.message || ""}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </Layout>
  );
};

export default ClassePage;
